import { useTranslation } from 'react-i18next';
import { FormikProvider, useField, useFormik } from 'formik';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Color, Paragraph } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import BareLayout from 'packages/kakapo-ui-application/layouts/BareLayout';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import PrimaryButton from 'packages/kakapo-ui-kit/components/buttons/PrimaryButton';
import PublicForm from 'packages/kakapo-ui-kit/components/forms/layouts/PublicForm';
import { ButtonSize } from 'packages/kakapo-ui-kit/types/Button';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';
import Toggle from 'packages/kakapo-ui-kit/components/forms/controls/Toggle';

const Title = styled(Box)`
	font-size: 24px;
	font-weight: bold;
`;

function CookieWarningConsent() {
	const { t } = useTranslation();
	const [{ value }, , { setValue }] = useField<boolean>('consent');

	return (
		<Flex alignItems="center">
			<Toggle checked={value} onChange={() => setValue(!value)} name={''} value={''} unspaced />
			<Box ml={2} onClick={() => setValue(!value)}>
				<Color color="slateBlue">{t('privacyConsent.allowUsageStatistics')}</Color>
			</Box>
		</Flex>
	);
}

export default function PrivacyConsent() {
	const { t } = useTranslation();
	const { language } = useLocalize();

	const formik = useFormik({
		initialValues: {
			consent: false,
		},
		onSubmit: () => {},
	});

	const [update, { loading }] = graphql.useUpdatePrivacyConsentMutation({
		variables: {
			input: {
				allowsUsageStatistics: formik.values.consent,
				version: '1.5',
			},
		},
	});

	const descriptions = t('privacyConsent.description').split('<br />');

	return (
		<BareLayout>
			<FormikProvider value={formik}>
				<PublicForm empty>
					<Title>{t('privacyConsent.title')}</Title>
					<Box mt={2}>
						<HorizontalRule />
					</Box>
					{/* <Box my={3}>{language.id === 'nl_NL' ? <PrivacyConsentNL /> : <PrivacyConsentEN />}</Box> */}
					<Paragraph my={3}>
						{descriptions.map((text, index) => (
							<>
								{text}
								{index !== descriptions.length - 1 && <br />}
							</>
						))}{' '}
						<a
							href={`https://terms.ontreams.com/Treams+App+-+Privacy+statement+Treams+B.V.+2021+v1.5+(${
								language.id === 'nl_NL' ? 'NL' : 'EN'
							}).pdf`}
							target="_blank"
							rel="noreferrer"
						>
							{t('privacyConsent.policies.privacy.title')}
						</a>{' '}
						&amp;{' '}
						<a
							href={`https://terms.ontreams.com/Treams+App+-+Cookie+policy+v1.5+(${
								language.id === 'nl_NL' ? 'NL' : 'EN'
							}).pdf`}
							target="_blank"
							rel="noreferrer"
						>
							{t('privacyConsent.policies.cookies.title')}
						</a>
						.
					</Paragraph>
					<HorizontalRule />
					<Box my={3}>
						<CookieWarningConsent />
					</Box>
					<Flex justifyContent="flex-end" alignItems="cenfleter" mt={3} mb={0}>
						<Box flexShrink={0} flexGrow={0} ml={4}>
							{loading ? (
								<Spinner isLoading size="18px" verticalMargin={0} />
							) : (
								<PrimaryButton
									size={ButtonSize.Medium}
									onClick={async () => {
										await update();
									}}
								>
									{t('privacyConsent.accept')}
								</PrimaryButton>
							)}
						</Box>
					</Flex>
				</PublicForm>
			</FormikProvider>
		</BareLayout>
	);
}
